import type { RadiationPatternPoint } from '@freedom/shared';
import { antennaDirectionalGainDbi, approximatePatternAttenuation, interpolateRadiationPattern } from './equipment.js';
import { freeSpacePathLossDb, fresnelRadiusM } from './index.js';
import { rainSpecificAttenuation } from './rain.js';

export const NO_COVERAGE_DBM = -127;

export interface CoverageCalculationInput {
  rows: number;
  cols: number;
  resolutionM: number;
  terrainElevationsM: Float32Array;
  originRow: number;
  originCol: number;
  originGroundElevationM: number;
  antennaHeightM: number;
  receiverHeightM: number;
  maxCoverageRadiusM: number;
  frequencyGHz: number;
  txPowerDbm: number;
  cableLossDb: number;
  additionalLossDb: number;
  receiverAntennaGainDbi: number;
  antennaPeakGainDbi: number;
  horizontalBeamwidthDeg: number;
  verticalBeamwidthDeg: number;
  frontToBackDb: number;
  horizontalPattern: RadiationPatternPoint[];
  verticalPattern: RadiationPatternPoint[];
  azimuthDeg: number;
  mechanicalTiltDeg: number;
  electricalTiltDeg: number;
  rainRateMmPerHour: number;
  clutterLossDb: number;
  minimumSignalDbm: number;
  kFactor: number;
}

export interface CoverageCalculationResult {
  values: Float32Array;
  minimumDbm: number;
  maximumDbm: number;
  coveredCellCount: number;
}

interface CellGeometry { index: number; distanceM: number; bearingDeg: number; radialBin: number; terrainM: number }

function angleDifferenceDeg(angleDeg: number, referenceDeg: number): number {
  return ((angleDeg - referenceDeg + 540) % 360) - 180;
}

function directionalGain(input: CoverageCalculationInput, horizontalOffsetDeg: number, verticalOffsetDeg: number): number {
  const horizontalAttenuation = input.horizontalPattern.length > 0
    ? interpolateRadiationPattern(input.horizontalPattern, horizontalOffsetDeg, true)
    : approximatePatternAttenuation(horizontalOffsetDeg, input.horizontalBeamwidthDeg, input.frontToBackDb);
  const verticalAttenuation = input.verticalPattern.length > 0
    ? interpolateRadiationPattern(input.verticalPattern, verticalOffsetDeg, false)
    : approximatePatternAttenuation(verticalOffsetDeg, input.verticalBeamwidthDeg, input.frontToBackDb);
  return input.antennaPeakGainDbi + horizontalAttenuation + verticalAttenuation;
}

export function calculateCoverageMatrix(input: CoverageCalculationInput, onProgress?: (progress: number) => void): CoverageCalculationResult {
  if (input.rows <= 0 || input.cols <= 0 || input.terrainElevationsM.length !== input.rows * input.cols) throw new RangeError('Coverage terrain matrix dimensions do not match');
  if (input.maxCoverageRadiusM <= 0 || input.resolutionM <= 0) throw new RangeError('Coverage radius and resolution must be positive');
  const values = new Float32Array(input.rows * input.cols);
  values.fill(NO_COVERAGE_DBM);
  const radialBinCount = 720;
  const radialBins: CellGeometry[][] = Array.from({ length: radialBinCount }, () => []);
  for (let row = 0; row < input.rows; row += 1) {
    for (let col = 0; col < input.cols; col += 1) {
      const northM = (input.originRow - row) * input.resolutionM;
      const eastM = (col - input.originCol) * input.resolutionM;
      const distanceM = Math.hypot(eastM, northM);
      if (distanceM > input.maxCoverageRadiusM || distanceM < input.resolutionM / 2) continue;
      const bearingDeg = ((Math.atan2(eastM, northM) * 180 / Math.PI) + 360) % 360;
      const radialBin = Math.min(radialBinCount - 1, Math.floor(bearingDeg / 360 * radialBinCount));
      const index = row * input.cols + col;
      radialBins[radialBin]?.push({ index, distanceM, bearingDeg, radialBin, terrainM: input.terrainElevationsM[index] ?? input.originGroundElevationM });
    }
  }
  const originIndex = Math.min(values.length - 1, Math.max(0, input.originRow * input.cols + input.originCol));
  values[originIndex] = Math.max(input.minimumSignalDbm, input.txPowerDbm - input.cableLossDb);
  const transmitterElevationM = input.originGroundElevationM + input.antennaHeightM;
  const rain = rainSpecificAttenuation({ frequencyGHz: input.frequencyGHz, rainRateMmPerHour: input.rainRateMmPerHour, polarization: 'horizontal' });
  let minimumDbm = Number.POSITIVE_INFINITY;
  let maximumDbm = Number.NEGATIVE_INFINITY;
  let coveredCellCount = 1;
  for (let binIndex = 0; binIndex < radialBins.length; binIndex += 1) {
    const cells = radialBins[binIndex];
    if (!cells) continue;
    cells.sort((left, right) => left.distanceM - right.distanceM);
    let maximumTerrainAngleRad = Number.NEGATIVE_INFINITY;
    for (const cell of cells) {
      const effectiveEarthDropM = cell.distanceM ** 2 / (2 * 6_371_000 * input.kFactor);
      const targetElevationM = cell.terrainM + input.receiverHeightM;
      const targetAngleRad = Math.atan2(targetElevationM - transmitterElevationM - effectiveEarthDropM, cell.distanceM);
      const terrainAngleRad = Math.atan2(cell.terrainM - transmitterElevationM - effectiveEarthDropM, cell.distanceM);
      const midpointFresnelM = fresnelRadiusM(input.frequencyGHz, cell.distanceM / 2, cell.distanceM / 2);
      const requiredFresnelAngleRad = Math.atan2(midpointFresnelM * 0.6, cell.distanceM / 2);
      const clearanceAngleRad = targetAngleRad - maximumTerrainAngleRad;
      const shadowLossDb = maximumTerrainAngleRad === Number.NEGATIVE_INFINITY ? 0 : clearanceAngleRad < 0 ? 25 + Math.min(25, Math.abs(clearanceAngleRad) * 180 / Math.PI * 3) : clearanceAngleRad < requiredFresnelAngleRad ? 12 * (1 - clearanceAngleRad / requiredFresnelAngleRad) : 0;
      const horizontalOffsetDeg = angleDifferenceDeg(cell.bearingDeg, input.azimuthDeg);
      const targetAngleDeg = targetAngleRad * 180 / Math.PI;
      const beamCenterDeg = -(input.mechanicalTiltDeg + input.electricalTiltDeg);
      const verticalOffsetDeg = targetAngleDeg - beamCenterDeg;
      const transmitGainDbi = directionalGain(input, horizontalOffsetDeg, verticalOffsetDeg);
      const distanceKm = cell.distanceM / 1_000;
      const signalDbm = input.txPowerDbm + transmitGainDbi + input.receiverAntennaGainDbi - input.cableLossDb
        - freeSpacePathLossDb(input.frequencyGHz, distanceKm) - input.additionalLossDb - input.clutterLossDb - shadowLossDb
        - rain.specificAttenuationDbPerKm * distanceKm;
      if (signalDbm >= input.minimumSignalDbm) {
        values[cell.index] = signalDbm;
        minimumDbm = Math.min(minimumDbm, signalDbm);
        maximumDbm = Math.max(maximumDbm, signalDbm);
        coveredCellCount += 1;
      }
      maximumTerrainAngleRad = Math.max(maximumTerrainAngleRad, terrainAngleRad);
    }
    if (binIndex % 36 === 0) onProgress?.(binIndex / radialBinCount);
  }
  onProgress?.(1);
  return {
    values,
    minimumDbm: Number.isFinite(minimumDbm) ? minimumDbm : NO_COVERAGE_DBM,
    maximumDbm: Number.isFinite(maximumDbm) ? maximumDbm : NO_COVERAGE_DBM,
    coveredCellCount,
  };
}

export function coverageDirectionalGainControl(input: Pick<CoverageCalculationInput, 'antennaPeakGainDbi' | 'horizontalBeamwidthDeg' | 'verticalBeamwidthDeg' | 'frontToBackDb' | 'horizontalPattern' | 'verticalPattern'>, horizontalOffsetDeg: number, verticalOffsetDeg: number): number {
  return antennaDirectionalGainDbi({ id: 'coverage-control', manufacturerId: 'coverage', manufacturerName: 'coverage', model: 'coverage', sku: null, roles: [], lifecycle: 'unknown', compatibilityTags: [], supportedBands: [], minFrequencyMHz: 1, maxFrequencyMHz: 1_000_000, peakGainDbi: input.antennaPeakGainDbi, horizontalBeamwidthDeg: input.horizontalBeamwidthDeg, verticalBeamwidthDeg: input.verticalBeamwidthDeg, polarization: 'dual', frontToBackDb: input.frontToBackDb, patternHorizontal: input.horizontalPattern, patternVertical: input.verticalPattern, patternApproximate: input.horizontalPattern.length === 0 || input.verticalPattern.length === 0, notes: '', sourceUrl: null, sourceTitle: null, sourceRetrievedOn: null, catalogVersion: null, specStatus: 'engineering-profile', isBuiltIn: true, isCustom: false, isActive: true }, horizontalOffsetDeg, verticalOffsetDeg).gainDbi;
}
